import React from 'react';
import { ProductListItem } from '../types/ProductTypes';

/**
 * Sorts products by relevance to the search term.
 * 
 * Priority (lower = better):
 * 1. Trade name starts with search term (position 0)
 * 2. Trade name contains search term (position > 0)
 * 3. Category name match (position + 0.01 offset)
 * 4. Charterer / CAS number match (position + 0.02 offset)
 * 5. Synonym match (position + 0.03 offset)
 * 
 * Products with no match in any visible field sort last.
 * This mirrors the old CommodityBook search relevance algorithm.
 */
export function sortByRelevance(products: ProductListItem[], searchTerm: string): ProductListItem[] {
    if (!searchTerm || !searchTerm.trim()) {
        return products;
    }

    const term = searchTerm.toLowerCase().trim();

    const scored = products.map((product) => {
        const scores: number[] = [];

        // Trade name match
        const tradeName = (product.tradeName || '').toLowerCase();
        const tradeIdx = tradeName.indexOf(term);
        if (tradeIdx >= 0) {
            // Exact full match gets 0, starts-with gets position, contains gets position + 0.1
            const score = tradeName === term ? 0 : tradeIdx === 0 ? 0.05 : tradeIdx + 0.1;
            scores.push(score);
        }

        // Category name match
        const categoryName = (product.categoryName || '').toLowerCase();
        const catIdx = categoryName.indexOf(term);
        if (catIdx >= 0) {
            const score = categoryName === term ? 0.01 : catIdx + 0.01 + 0.1;
            scores.push(score);
        }

        // Charterer name match
        const chartererName = (product.chartererName || '').toLowerCase();
        const chartIdx = chartererName.indexOf(term);
        if (chartIdx >= 0) {
            const score = chartererName === term ? 0.02 : chartIdx + 0.02 + 0.1;
            scores.push(score);
        }

        // CAS number match
        const casNumber = (product.casNumber || '').toLowerCase();
        const casIdx = casNumber.indexOf(term);
        if (casIdx >= 0) {
            const score = casNumber === term ? 0.02 : casIdx + 0.02 + 0.1;
            scores.push(score);
        }

        // Synonym match
        const synonym = (product.synonym || '').toLowerCase();
        const synIdx = synonym.indexOf(term);
        if (synIdx >= 0) {
            const score = synonym === term ? 0.03 : synIdx + 0.03 + 0.1;
            scores.push(score);
        }

        // Best (lowest) score across all fields; no match = very high
        const bestScore = scores.length > 0 ? Math.min(...scores) : 9999;

        return { product, bestScore };
    });

    scored.sort((a, b) => a.bestScore - b.bestScore);

    return scored.map((s) => s.product);
}

/**
 * Escapes characters that have special meaning in HTML.
 */
function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Highlights all occurrences of `searchTerm` within `text`.
 * Returns a React element with matched portions wrapped in <mark>.
 * If no match or no search term, returns the plain text string.
 */
export function highlightMatch(text: string | undefined | null, searchTerm: string): React.ReactNode {
    if (!text) return text ?? '';
    if (!searchTerm || !searchTerm.trim()) return text;

    const term = searchTerm.trim();
    // Escape regex special characters in the search term
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);

    if (parts.length === 1) {
        // No match found
        return text;
    }

    return (
        <>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <mark key={i} style={{ background: '#fff3b0', color: 'inherit', padding: 0, borderRadius: '2px' }}>
                        {part}
                    </mark>
                ) : (
                    <React.Fragment key={i}>{part}</React.Fragment>
                )
            )}
        </>
    );
}
